import React, { useCallback, useRef, useState } from "react";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import {
  X,
  RotateCw,
  Crop as CropIcon,
  SkipForward,
  Loader2,
} from "lucide-react";

const ASPECT_OPTIONS = [
  { id: "free", label: "Libero", value: undefined },
  { id: "16:9", label: "16:9", value: 16 / 9 },
  { id: "4:3", label: "4:3", value: 4 / 3 },
  { id: "1:1", label: "1:1", value: 1 },
];

/**
 * Modal per ritagliare (e opzionalmente ruotare) un'immagine prima
 * dell'upload.
 *
 * Props:
 *  - file: File (l'immagine originale)
 *  - onCancel: () => void        chiudi senza caricare
 *  - onSkip:   (file) => void    carica l'originale senza modifiche
 *  - onConfirm: (blob) => void   carica la versione ritagliata
 */
const ImageCropDialog = ({ file, onCancel, onSkip, onConfirm }) => {
  const [src, setSrc] = useState(() => URL.createObjectURL(file));
  const [aspect, setAspect] = useState(undefined);
  const [rotation, setRotation] = useState(0);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const [processing, setProcessing] = useState(false);
  const imgRef = useRef(null);

  // Quando l'immagine e' caricata, inizializza un crop centrato al 90%
  const onImageLoad = useCallback(
    (e) => {
      const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
      if (aspect) {
        const c = centerCrop(
          makeAspectCrop({ unit: "%", width: 90 }, aspect, w, h),
          w,
          h
        );
        setCrop(c);
      } else {
        // Libero: inizializza a un rettangolo centrato al 90%
        setCrop({ unit: "%", x: 5, y: 5, width: 90, height: 90 });
      }
    },
    [aspect]
  );

  const onAspectChange = (v) => {
    setAspect(v);
    if (imgRef.current && v) {
      const { naturalWidth: w, naturalHeight: h } = imgRef.current;
      const c = centerCrop(
        makeAspectCrop({ unit: "%", width: 90 }, v, w, h),
        w,
        h
      );
      setCrop(c);
    }
  };

  const rotate = () => setRotation((r) => (r + 90) % 360);

  const buildCroppedBlob = async () => {
    const image = imgRef.current;
    if (!image || !completedCrop) return null;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const cropW = completedCrop.width * scaleX;
    const cropH = completedCrop.height * scaleY;

    // Canvas ruotato se rotation !== 0
    const rotated = rotation % 180 !== 0;
    const canvas = document.createElement("canvas");
    canvas.width = rotated ? cropH : cropW;
    canvas.height = rotated ? cropW : cropH;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    ctx.imageSmoothingQuality = "high";
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      cropW,
      cropH,
      -cropW / 2,
      -cropH / 2,
      cropW,
      cropH
    );

    // Sceglie il formato in base al mime originale (jpeg per default)
    const type = file.type === "image/png" ? "image/png" : "image/jpeg";
    const quality = type === "image/jpeg" ? 0.92 : undefined;

    return new Promise((resolve) =>
      canvas.toBlob((blob) => resolve(blob), type, quality)
    );
  };

  const handleConfirm = async () => {
    if (!completedCrop) {
      // Se non ha fatto nessun crop, usiamo l'originale
      onSkip?.(file);
      return;
    }
    setProcessing(true);
    try {
      const blob = await buildCroppedBlob();
      if (!blob) {
        onCancel?.();
        return;
      }
      // Preserva l'estensione originale
      const ext = file.name.split(".").pop() || "jpg";
      const newFile = new File(
        [blob],
        file.name.replace(/\.[^.]+$/, "") + "-cropped." + ext,
        { type: blob.type }
      );
      onConfirm?.(newFile);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[10000] bg-black/90 backdrop-blur-sm flex items-center justify-center p-3"
      role="dialog"
      aria-modal="true"
      aria-label="Ritaglia immagine"
    >
      <div className="bg-white rounded-2xl overflow-hidden shadow-2xl w-full max-w-3xl max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <CropIcon className="w-5 h-5 text-[#0A1F44]" />
            <h3 className="font-montserrat font-bold text-[#0A1F44]">
              Ritaglia immagine
            </h3>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-[#0A1F44]"
            aria-label="Chiudi"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2 p-3 border-b border-gray-100 bg-gray-50">
          <div className="inline-flex items-center gap-1 bg-white rounded-lg p-1 border border-gray-200">
            <span className="text-xs text-gray-500 font-semibold ml-2 mr-1">
              Formato
            </span>
            {ASPECT_OPTIONS.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => onAspectChange(o.value)}
                className={`h-8 px-3 rounded-md text-xs font-montserrat font-semibold transition-colors ${
                  (aspect || undefined) === o.value
                    ? "bg-[#0A1F44] text-white"
                    : "text-gray-600 hover:text-[#0A1F44]"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={rotate}
            className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg bg-white border border-gray-200 hover:border-[#0A1F44] text-sm font-montserrat font-semibold text-[#0A1F44] transition-colors"
          >
            <RotateCw className="w-4 h-4" />
            Ruota 90°
          </button>
        </div>

        {/* Area crop */}
        <div className="flex-1 bg-gray-950 flex items-center justify-center overflow-auto p-4 min-h-[280px]">
          <ReactCrop
            crop={crop}
            onChange={(c) => setCrop(c)}
            onComplete={(c) => setCompletedCrop(c)}
            aspect={aspect}
            keepSelection
          >
            <img
              ref={imgRef}
              src={src}
              alt="Immagine da ritagliare"
              onLoad={onImageLoad}
              style={{
                maxHeight: "60vh",
                transform: `rotate(${rotation}deg)`,
              }}
            />
          </ReactCrop>
        </div>

        {/* Footer */}
        <div className="flex flex-wrap items-center justify-end gap-2 p-4 border-t border-gray-100 bg-gray-50">
          <button
            type="button"
            onClick={() => onSkip?.(file)}
            disabled={processing}
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg text-sm font-montserrat font-semibold text-gray-600 hover:text-[#0A1F44] transition-colors"
          >
            <SkipForward className="w-4 h-4" />
            Salta crop
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={processing || !completedCrop}
            className="inline-flex items-center gap-1.5 h-10 px-5 rounded-lg text-sm font-montserrat font-bold bg-[#F4C542] hover:bg-[#e6b838] text-[#0A1F44] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {processing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <CropIcon className="w-4 h-4" />
            )}
            Ritaglia e carica
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageCropDialog;
